import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { Search, PenTool, Hammer, Rocket } from 'lucide-react';

const phases = [
  {
    icon: Search,
    title: 'Discovery',
    subtitle: 'Menemukan "Why"',
    desc: 'Audit pasar, pemetaan persona, dan sesi mendalam untuk memahami tujuan sejati bisnis Anda.',
    color: '#00FF85',
    lightColor: '#0D9488',
    span: 'md:col-span-4 lg:col-span-5',
  },
  {
    icon: PenTool,
    title: 'Blueprint',
    subtitle: 'Merancang "How"',
    desc: 'Roadmap teknis berbasis "Why". Setiap keputusan desain dan fitur terhubung ke tujuan bisnis.',
    color: '#3A82FF',
    lightColor: '#2563EB',
    span: 'md:col-span-4 lg:col-span-7',
  },
  {
    icon: Hammer,
    title: 'Build',
    subtitle: 'Mewujudkan "What"',
    desc: 'Teknologi legal & modern. Kode bersih, performa tinggi, SEO-ready. Tanpa jalan pintas.',
    color: '#FF0099',
    lightColor: '#D97706',
    span: 'md:col-span-4 lg:col-span-7',
  },
  {
    icon: Rocket,
    title: 'Launch & Grow',
    subtitle: 'Terus Berkembang',
    desc: 'Optimasi berkelanjutan + integrasi AI. Website Anda bukan produk jadi—ia terus bertumbuh.',
    color: '#00FF85',
    lightColor: '#0D9488',
    span: 'md:col-span-4 lg:col-span-5',
  },
];

export default function SolutionSection() {
  const { theme } = useTheme();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      data-testid="solution-section"
      ref={ref}
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 lg:py-40">
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
        >
          <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: 'var(--accent-primary)' }}>
            The How
          </span>
        </motion.div>

        <motion.h2
          className="font-heading text-3xl md:text-5xl font-semibold tracking-tight leading-tight max-w-3xl mb-6"
          style={{ color: 'var(--text-primary)' }}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1 }}
        >
          {theme === 'dark'
            ? 'Metodologi kami dimulai dari pertanyaan yang paling penting: Mengapa?'
            : 'Proses terstruktur yang terbukti menghasilkan. Dimulai dari "Mengapa".'}
        </motion.h2>

        <motion.p
          className="text-base md:text-lg leading-relaxed max-w-2xl mb-16"
          style={{ color: 'var(--text-secondary)' }}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
        >
          The Golden Circle: Why → How → What. Setiap pixel, setiap baris kode, dimulai dari tujuan bisnis Anda.
        </motion.p>

        {/* Golden Circle Visual */}
        <motion.div
          className="flex justify-center mb-20"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <div className="relative w-64 h-64 md:w-80 md:h-80">
            {/* Outer - What */}
            <motion.div
              className="absolute inset-0 rounded-full flex items-end justify-center pb-6"
              style={{
                border: `2px solid ${theme === 'dark' ? 'rgba(255,0,153,0.3)' : 'rgba(217,119,6,0.3)'}`,
                background: theme === 'dark' ? 'rgba(255,0,153,0.03)' : 'rgba(217,119,6,0.03)',
              }}
              initial={{ scale: 0 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <span className="font-mono text-xs tracking-wider uppercase" style={{ color: theme === 'dark' ? '#FF0099' : '#D97706' }}>What</span>
            </motion.div>
            {/* Middle - How */}
            <motion.div
              className="absolute rounded-full flex items-end justify-center pb-4"
              style={{
                top: '15%', left: '15%', right: '15%', bottom: '15%',
                border: `2px solid ${theme === 'dark' ? 'rgba(58,130,255,0.4)' : 'rgba(37,99,235,0.4)'}`,
                background: theme === 'dark' ? 'rgba(58,130,255,0.03)' : 'rgba(37,99,235,0.03)',
              }}
              initial={{ scale: 0 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <span className="font-mono text-xs tracking-wider uppercase" style={{ color: 'var(--accent-secondary)' }}>How</span>
            </motion.div>
            {/* Inner - Why */}
            <motion.div
              className="absolute rounded-full flex items-center justify-center"
              style={{
                top: '30%', left: '30%', right: '30%', bottom: '30%',
                border: `2px solid var(--accent-primary)`,
                background: theme === 'dark' ? 'rgba(0,255,133,0.08)' : 'rgba(13,148,136,0.08)',
              }}
              initial={{ scale: 0 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <span className="font-heading text-lg font-bold" style={{ color: 'var(--accent-primary)' }}>Why</span>
            </motion.div>
          </div>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
          {phases.map((phase, i) => {
            const Icon = phase.icon;
            const accentColor = theme === 'dark' ? phase.color : phase.lightColor;
            return (
              <motion.div
                key={i}
                className={`glass-card p-8 group cursor-default ${phase.span}`}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 + i * 0.15 }}
                data-testid={`solution-phase-${i}`}
                style={{
                  borderColor: theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-5 transition-all duration-300 group-hover:scale-110"
                  style={{ background: `${accentColor}15`, color: accentColor }}
                >
                  <Icon size={20} />
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-xs tracking-wider" style={{ color: accentColor }}>0{i + 1}</span>
                  <h3 className="font-heading text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>{phase.title}</h3>
                </div>
                <p className="text-xs font-mono tracking-wider uppercase mb-3" style={{ color: 'var(--text-secondary)' }}>{phase.subtitle}</p>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{phase.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
